import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LandLordContext from "../../context/landlord/LandLordContext";
import useTranslator from "../../hooks/useTranslator";
import AuthStepHeader from "../../components/Auth/AuthStepHeader";
import { Badge, Button, Checkbox } from "../../components/design";
import "./tenantCheckSteps.css";

// One "what do you want to check" option: a bordered row with a checkbox.
// Always-included rows are ticked, locked and show a small label on the right.
const Row = ({ title, subtitle, selected, disabled, badge, onClick }) => (
  <div
    className={`tc-option ${disabled ? "is-locked" : selected ? "is-selected" : ""}`}
  >
    <Checkbox
      checked={!!selected}
      disabled={disabled}
      onChange={() => onClick?.()}
      label={title}
      description={subtitle}
    />
    {badge && <Badge tone="neutral">{badge}</Badge>}
  </div>
);

const AdditionalChecksModal = ({
  user,
  step,
  additionalInfo,
  setAdditionalInfo,
  formHandler,
  updateUserProfile,
  getNextStep,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { updateAdditionalInfo } = useContext(LandLordContext);
  const [loading, setLoading] = useState(false);
  const hasKyc = additionalInfo.includes("TENANT_KYC");
  const hasSkor =
    additionalInfo.includes("TENANT_SKOR") ||
    additionalInfo.includes("GUARANTOR_SKOR");

  const toggle = (key) =>
    setAdditionalInfo((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const toggleFinancialStudy = () => {
    setAdditionalInfo((prev) => {
      const selected = prev.includes("TENANT_SKOR");

      if (selected) {
        return prev.filter(
          (key) => !["TENANT_SKOR", "GUARANTOR_SKOR"].includes(key)
        );
      }

      return [...prev.filter((key) => key !== "GUARANTOR_SKOR"), "TENANT_SKOR"];
    });
  };

  const STEP_TO_NUM = {
    checks: 1,
    basic: 2,
    identity: 3,
    documents: 5,
    financial: 6,
  };
  const handleNext = async () => {
    try {
      setLoading(true);
      const _stepId = getNextStep(step);
      const _step = _stepId ? (STEP_TO_NUM[_stepId] ?? _stepId) : null;
      if (user?._id) {
        await updateAdditionalInfo({
          id: user._id,
          additional_info: additionalInfo,
        });
        if (_step) await updateUserProfile({ id: user._id, step: _step });
      }
      formHandler.next();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tc-step-page">
      <div className="tc-step">
        <AuthStepHeader
          onBack={() => navigate(`/${workspaceId}/created-tenant`)}
          title={_t("whatDoYouWantToCheck", "What do you want to check?")}
          subtitle={_t(
            "checksSubtitle",
            "Basic data and document analysis are always included. Add more checks if you need them."
          )}
        />

        <div className="tc-options">
          <Row
            title={_t("basicData", "Basic data")}
            badge={_t("alwaysIncluded", "Always included")}
            selected
            disabled
          />
          <Row
            title={_t("documentAnalysis", "Document analysis")}
            badge={_t("alwaysIncluded", "Always included")}
            selected
            disabled
          />
          <Row
            title={_t(
              "identityVerificationId",
              "Identity verification (ID document)"
            )}
            subtitle={_t(
              "verifyIdentitySubtitle",
              "Verify their identity with a photo of their ID document"
            )}
            selected={hasKyc}
            onClick={() => toggle("TENANT_KYC")}
          />
          <Row
            title={_t("financialStudy", "Financial study")}
            subtitle={_t(
              "financialStudySubtitle",
              "Assess their income with a bank statement"
            )}
            selected={hasSkor}
            onClick={toggleFinancialStudy}
          />
        </div>

        <div className="tc-step-actions">
          <Button size="lg" loading={loading} onClick={handleNext}>
            {_t("startCheck", "Start check")}
          </Button>
        </div>
      </div>
    </div>
  );
};
export default AdditionalChecksModal;
